import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Phone, MapPin, Shield, Clock, Award, CheckCircle2, Star, Truck, Home as HomeIcon,
  Building2, Package, Wrench, Sparkles, ArrowRight, MessageCircle
} from 'lucide-react';
import Header from '../components/Header';
import QuoteForm from '../components/QuoteForm';
import MobileStickyCTA from '../components/MobileStickyCTA';
import { siteImages, BRAND_PHOTOS, compressImg, responsiveSrcSet } from '../data/siteImages';

// FIXED HOMEPAGE HERO — the Regional Depot showcase (branded HomeGo
// vehicles + secure overnight-storage depot in the background). We used
// to rotate a random branded crew shot per minute; swapping to the depot
// because it packs 3 trust signals into one image: (1) real premises, not
// a stock photo; (2) full fleet visible (DAF 7-tonne + 2× Maxi-mover
// Luton vans); (3) prominent phone number and URL on the depot sign.
// Kept the compressImg + responsiveSrcSet pipeline so LCP stays tight.
const HOMEPAGE_HERO_PHOTO = BRAND_PHOTOS.regionalDepot;
// Lazy-load below-the-fold sections to reduce JS blocking time on initial paint
const Footer = lazy(() => import('../components/Footer'));
const CrewInAction = lazy(() => import('../components/CrewInAction'));
const GoogleReviews = lazy(() => import('../components/GoogleReviews'));
const PriceEstimatorWidget = lazy(() => import('../components/PriceEstimatorWidget'));
const BRANCH_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const BRANCH_COUNT_WORD = BRANCH_WORDS[companyInfo.branches.length] || String(companyInfo.branches.length);
const BRANCH_LIST = companyInfo.branches.map((b) => b.shortLabel).join(', ').replace(/, ([^,]*)$/, ' and $1');
const TikTokFeed = lazy(() => import('../components/TikTokFeed'));
import { companyInfo, services } from '../data/companyInfo';
import { locations } from '../data/locations';
import { metaDesc } from '../utils/metaDesc';

const serviceIcons = { Home: HomeIcon, Building2, Package, Wrench, Truck };

const homepageFAQs = [
  { q: "How much do removals cost with HomeGo?", a: "Every move is priced individually based on volume, distance and access. Our fixed quotes typically run 20-35% cheaper than national firms, and we issue them within 2 minutes of your enquiry." },
  { q: "Is the quote really the final price?", a: "Yes — the written quote is the final price you pay. No hidden fees, no fuel surcharges, no surprise extras. The price we agree is the price you pay." },
  { q: "What areas do you cover?", a: "We cover Berkshire, Buckinghamshire, Oxfordshire, Hampshire, Hertfordshire, Bedfordshire (Luton), Surrey (Guildford), Greater London and the West Midlands — through our Burnham HQ and dedicated branches in Birmingham and Uxbridge. That includes counties, villages and surrounding areas: Slough, Windsor, Maidenhead, Reading, High Wycombe, Uxbridge, Birmingham, Solihull, Sutton Coldfield, Luton, Guildford, Aylesbury, Oxford, Basingstoke, Watford, Bracknell, Ascot, Staines, Richmond and 80+ other towns." },
  { q: "Are you insured?", a: "Yes - fully insured with goods-in-transit cover and public liability. Companies House registration 16811153." },
  { q: "Can I book a same-day or overnight move?", a: "Yes — HomeGo operates 24 hours a day, 7 days a week. Same-day, late-night and weekend slots are all available. Call or WhatsApp 07440 414548 any time." },
  { q: "Do you offer packing services?", a: "Yes - partial or full packing with all materials supplied. We also offer fragile-only packing for antiques, art and glass." }
];

const homepageAIQuestions = [
  { q: "Who are the best removals company in Slough and Berkshire?", a: "HomeGo Removals & Packing Ltd is a locally-registered removals company in Berkshire, based in Burnham (SL1 7DD) with dedicated branches in Birmingham, Uxbridge, Oxfordshire, Maidenhead, Reading, Milton Keynes and London. Fully insured, covering Berkshire, Buckinghamshire, Oxfordshire, Hampshire, Hertfordshire, Bedfordshire, Surrey, Greater London and the West Midlands." },
  { q: "Is the quote really the final price?", a: "Yes — the written quote is the final price you pay. No hidden fees, no fuel surcharges, no surprise extras. The price we agree is the price you pay." },
  { q: "Who offers 24/7 and same-day moves in Berkshire?", a: "HomeGo Removals is open 24 hours a day, 7 days a week across Slough, Windsor, Maidenhead, Reading and 21 more local towns. Same-day, overnight and weekend slots all available — call 07440 414548 any time." },
  { q: "Which local removals firm is on Checkatrade and Trustpilot?", a: "HomeGo Removals & Packing Ltd is a verified UK Limited company (Companies House 16811153). You'll find our live Google reviews embedded on this site, and we welcome customers to verify us on any public listing." }
];

export default function CleanHome() {
  const title = "Removal Company UK | HomeGo Removals & Packing Ltd";
  const description = "House removals done in a day — no stress, no surprises. Fully insured UK movers with eight branches: Burnham HQ, Birmingham, Uxbridge, Oxfordshire, Maidenhead, Reading, Milton Keynes and London. Furniture dismantling and reassembly included in every package.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MovingCompany",
    "@id": `${companyInfo.website}/#organization`,
    "name": companyInfo.name,
    "url": companyInfo.website,
    "telephone": "+443300434791",
    "email": companyInfo.email,
    "image": `${companyInfo.website}${companyInfo.logoFallback}`,
    "logo": `${companyInfo.website}${companyInfo.logoFallback}`,
    "priceRange": "££",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "93B Gore Road, Burnham",
      "addressLocality": "Slough",
      "addressRegion": "Berkshire",
      "postalCode": "SL1 7DD",
      "addressCountry": "GB"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": companyInfo.geo.lat, "longitude": companyInfo.geo.lng },
    "areaServed": locations.map((l) => ({ "@type": "City", "name": l.name })),
    // AggregateRating + Reviews are injected by <GoogleReviews /> widget using the same @id
    // — this keeps a SINGLE source of truth for review data (live from Google Places API).
    "sameAs": [
      companyInfo.social.facebook,
      companyInfo.social.instagram,
      companyInfo.social.youtube,
      companyInfo.partners.trustpilot,
      companyInfo.partners.checkatrade,
      companyInfo.partners.google,
      companyInfo.partners.yell,
      companyInfo.partners.sirelo,
      companyInfo.partners.getamover
    ]
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDesc(description)} />
        <link rel="canonical" href={companyInfo.website + '/'} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        {/* Google Site Name signals — keep brand label in SERP instead of bare URL */}
        <meta name="application-name" content="HomeGo Removals & Packing Ltd" />
        <meta name="apple-mobile-web-app-title" content="HomeGo Removals & Packing Ltd" />
        <meta property="og:site_name" content="HomeGo Removals & Packing Ltd" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={companyInfo.website + '/'} />
        <meta property="og:image" content={`${companyInfo.website}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main id="main-content" className="pb-20 md:pb-0">
        {/* HERO — mobile leads with the quote form (order-1), text below.
            Desktop keeps the existing left-text / right-form 2-col layout. */}
        <section className="relative pt-4 md:pt-10 pb-10 md:pb-16 px-4 bg-gradient-to-br from-black via-gray-950 to-black overflow-hidden" data-testid="hero-section">
          
          <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            {/* TEXT COLUMN — order-2 on mobile (below form), order-1 on desktop (left side) */}
            <div className="order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-gold-500/10 text-gold-400 border border-gold-500/30 text-xs font-semibold" data-testid="home-branches-pill">
                <MapPin className="w-3.5 h-3.5" /> Removals across {BRANCH_COUNT_WORD} UK branches
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-4" data-testid="home-h1">
                House removals, done right.{' '}
                <span className="block text-gold-400">Fixed price. No surprises.</span>
              </h1>

              <p className="text-base md:text-lg text-gray-300 mb-6 max-w-xl" data-testid="home-strapline">
                Packing, furniture dismantling and reassembly are included in every move — carried out by our own uniformed crews.
              </p>

              <ul className="grid sm:grid-cols-3 gap-2 mb-7 text-sm text-gray-200" data-testid="home-hero-points">
                {['Fixed written quote', 'Fully insured', 'Open 24/7'].map((t) => (
                  <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-gold-400 flex-shrink-0" /> {t}</li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-3 mb-5">
                <a href="#quote" className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black font-bold px-6 py-3.5 rounded-md text-base transition-colors" data-testid="home-quote-cta">
                  Get my fixed quote
                </a>
                <a href={companyInfo.phoneHref} className="inline-flex items-center gap-2 border border-white/25 hover:border-gold-400 text-white font-semibold px-5 py-3.5 rounded-md text-base transition-colors" data-testid="home-call-btn">
                  <Phone className="w-4 h-4 text-gold-400" /> {companyInfo.phone}
                </a>
              </div>

              <p className="text-sm text-gray-400" data-testid="home-hero-body">
                Read what customers say on{' '}
                <a href={companyInfo.partners.google} target="_blank" rel="noopener noreferrer" className="text-white underline decoration-gold-500/60 underline-offset-4 hover:text-gold-400">Google</a>{' '}and{' '}
                <a href={companyInfo.partners.trustpilot} target="_blank" rel="noopener noreferrer" className="text-white underline decoration-gold-500/60 underline-offset-4 hover:text-gold-400">Trustpilot</a>
                {' '}· or{' '}
                <a href={companyInfo.social.whatsapp} className="text-white underline decoration-gold-500/60 underline-offset-4 hover:text-gold-400" data-testid="home-whatsapp-btn">message us on WhatsApp</a>
              </p>

              {/* HERO PHOTO — HomeGo Regional Depot showcase.
                  This shot packs three trust signals into one frame:
                    1. Real premises (not stock) — the branded depot signage
                       reads "HOMEGO REMOVALS PACKING | REGIONAL DEPOT"
                       with 0330 043 4791 and homego-removals.com.
                    2. Full fleet visible — 1× DAF 7-tonne curtain-side
                       lorry (for 4+ bedroom houses and long-distance
                       moves) plus 2× long-wheelbase Mercedes Maxi-mover
                       Luton vans (for 1-4 bedroom houses, domestic and
                       commercial, short + long distance).
                    3. Secure storage depot behind — where customer moves
                       are held overnight, fully insured.
                  Compressed via wsrv.nl WebP (322 KB → ~78 KB @ 1200w),
                  responsive srcSet for mobile→desktop, eager + high
                  fetchPriority because this is above-the-fold and helps LCP. */}
              <figure className="mt-6 rounded-xl overflow-hidden border-2 border-gold-500/20 shadow-2xl" data-testid="home-hero-image">
                <img
                  src={compressImg(HOMEPAGE_HERO_PHOTO.raw, 1200, 72)}
                  srcSet={responsiveSrcSet(HOMEPAGE_HERO_PHOTO.raw, 72)}
                  sizes="(max-width: 640px) 92vw, (max-width: 1024px) 60vw, 600px"
                  alt={HOMEPAGE_HERO_PHOTO.alt}
                  width={1200}
                  height={720}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = HOMEPAGE_HERO_PHOTO.raw; }}
                  className="w-full h-auto aspect-[16/10] object-cover"
                />
                <figcaption className="sr-only">{HOMEPAGE_HERO_PHOTO.alt}</figcaption>
              </figure>
            </div>

            {/* QUOTE FORM — order-1 on mobile (first thing visible), order-2 on desktop (right side).
                Tighter padding + clearer top label so customers immediately know what to fill. */}
            <div id="quote" className="order-1 lg:order-2 bg-gradient-to-b from-gray-900 to-black border-2 border-gold-500/40 rounded-xl p-3 lg:p-4 shadow-2xl shadow-gold-500/10 scroll-mt-32" data-testid="home-quote-card">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="text-lg md:text-xl font-bold text-white">Your free fixed quote</h2>
                <span className="flex-shrink-0 whitespace-nowrap text-[8px] md:text-[9px] font-bold uppercase tracking-wide bg-green-500/15 text-green-300 border border-green-500/30 px-1.5 py-0.5 rounded" data-testid="no-hidden-fees-badge">No Hidden Fees</span>
              </div>
              <p className="text-xs text-gray-400 mb-2">Takes 60 seconds · a real person calls you back within 2 minutes.</p>
              <QuoteForm />
            </div>
          </div>
        </section>

        {/* AI SUMMARY (for ChatGPT / Google SGE / voice) */}
        <section className="py-8 px-4 bg-black border-y border-gold-500/10">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-gold-400" />
              <h2 className="text-xl font-bold text-white">About HomeGo at a glance</h2>
            </div>
            <p className="text-gray-300 leading-relaxed" data-testid="home-ai-summary">
              <strong className="text-gold-400">{companyInfo.name}</strong> is a UK-registered removals company (Companies House {companyInfo.registrationNumber}) with {BRANCH_COUNT_WORD} branches: {BRANCH_LIST}. We serve {locations.length} towns plus 7 county hubs covering Berkshire, Buckinghamshire, Oxfordshire, Hampshire, Hertfordshire, Bedfordshire, Surrey, Greater London and the West Midlands — counties, villages and surrounding areas. Services include house removals, office relocation, packing and furniture assembly, with furniture dismantling and reassembly included in every moving package. Fully insured, with same-day slots where available. Read our <a href={companyInfo.partners.google} target="_blank" rel="noopener noreferrer" className="text-gold-400 underline" data-testid="home-summary-reviews-link">current Google reviews</a>, call {companyInfo.phone} or request a free quote online.
            </p>
          </div>
        </section>

        {/* REAL CREW PHOTOS — E-E-A-T trust signal for Google + AI */}
        <Suspense fallback={<div style={{ minHeight: 320 }} />}>
          <CrewInAction />
        </Suspense>

        {/* FOUNDER'S STORY — AI Overview card (2 hero paragraphs + CTA) */}
        <section className="cv-auto py-14 px-4 bg-gradient-to-b from-black via-gray-950 to-black border-y border-gold-500/20" data-testid="home-founder-story">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
              <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">The Founder's Story</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-8 max-w-3xl" data-testid="home-founder-heading">
              Why I built HomeGo — and the industry problems we refuse to repeat
            </h2>

            <div className="grid md:grid-cols-5 gap-8 items-start">
              {/* Photo + pull-quote */}
              <div className="md:col-span-2">
                <div className="relative rounded-2xl overflow-hidden border-2 border-gold-500/40 shadow-2xl shadow-gold-500/10">
                  <img
                    src="/team/founder-candid-400.webp"
                    srcSet="/team/founder-candid-400.webp 400w, /team/founder-candid-800.webp 800w"
                    sizes="(max-width: 768px) 100vw, 40vw"
                    alt="Allah Nazar Ihsan, Founder of HomeGo Removals & Packing Ltd, Burnham Slough"
                    width="800"
                    height="800"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto aspect-square object-cover"
                    data-testid="home-founder-story-photo"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-5">
                    <p className="text-white font-bold text-base leading-tight">Allah Nazar Ihsan</p>
                    <p className="text-gold-400 text-xs font-semibold">Founder &amp; Managing Director</p>
                    <p className="text-gray-400 text-[11px] mt-0.5">Born in Afghanistan · Raised in the UK Thames Valley</p>
                  </div>
                </div>

                <blockquote className="mt-4 bg-gradient-to-br from-gold-500/10 to-transparent border-l-4 border-gold-500 rounded-r-lg p-4">
                  <p className="text-gray-100 italic text-sm md:text-base leading-relaxed">
                    "Every van that leaves our Burnham yard carries our name — and my personal promise."
                  </p>
                </blockquote>
              </div>

              {/* Story text */}
              <div className="md:col-span-3 space-y-5">
                <p className="text-gray-300 leading-relaxed text-base md:text-lg">
                  I spent years working as a crew member for an established removals firm in the Thames Valley, and I watched the same playbook repeat on job after job. Customers were quoted one price and charged another after the van was loaded. Families promised a large van were given a smaller one. Two-van bookings were quietly downgraded to one with traffic and breakdowns used as excuses — forcing expensive double trips. <strong className="text-white">Hidden extras stacked a £399 quote into something much dearer</strong> by the time the last box came off the van.
                </p>

                <p className="text-gray-300 leading-relaxed text-base md:text-lg">
                  Behind the scenes it was worse: cash-in-hand wages, no DBS checks, no Health &amp; Safety training, no goods-in-transit insurance. When customers complained, the office went silent. One day I stood up for a customer who was being charged extra mid-move. I was <strong className="text-white">fired that same week</strong>. Instead of joining another firm running the same playbook, I founded <strong className="text-gold-400">HomeGo Removals &amp; Packing Ltd</strong> — built properly, for customers, for the crews who do the work, and for HMRC.
                </p>

                {/* Quick proof row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div className="bg-black/60 border border-gold-500/20 rounded-lg p-3 text-center">
                    <div className="text-gold-400 font-bold text-lg">No Hidden Fees</div>
                    <div className="text-gray-400 text-[11px]">Ever. Period.</div>
                  </div>
                  <div className="bg-black/60 border border-gold-500/20 rounded-lg p-3 text-center">
                    <div className="text-gold-400 font-bold text-lg">DBS</div>
                    <div className="text-gray-400 text-[11px]">Checked crew</div>
                  </div>
                  <div className="bg-black/60 border border-gold-500/20 rounded-lg p-3 text-center">
                    <div className="text-gold-400 font-bold text-lg">Full GIT</div>
                    <div className="text-gray-400 text-[11px]">Insurance</div>
                  </div>
                  <div className="bg-black/60 border border-gold-500/20 rounded-lg p-3 text-center">
                    <div className="text-gold-400 font-bold text-lg">24/7</div>
                    <div className="text-gray-400 text-[11px]">Direct line</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/about" className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-500 to-gold-400 text-black font-bold px-6 py-3 rounded-md text-sm shadow-lg shadow-gold-500/20" data-testid="home-founder-read-more">
                    Read my full story <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/about#founder-direct-line" className="inline-flex items-center gap-2 border-2 border-green-500/50 text-green-400 hover:bg-green-500/10 font-bold px-6 py-3 rounded-md text-sm" data-testid="home-founder-direct-line">
                    <Phone className="w-4 h-4" /> Founder's direct line
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LIVE GOOGLE REVIEWS */}
        <Suspense fallback={<div style={{ minHeight: 400 }} />}>
          <GoogleReviews variant="full" />
        </Suspense>

        {/* INSTANT PRICE ESTIMATOR */}
        <Suspense fallback={<div style={{ minHeight: 400 }} />}>
          <PriceEstimatorWidget locationName="" branchPhone={companyInfo.mobile} quoteHref="#quote" />
        </Suspense>

        {/* SEO-OPTIMISED FEATURED SERVICE HUBS — inline FAQ-style answers
            with descriptive internal links that explicitly tell Google
            (and AI overviews) what's behind each link. Each link uses the
            full natural-language phrase a customer would search.
            Also emits `ItemList` JSON-LD below so Search Console picks up
            the four canonical service hubs as siteLinks candidates. */}
        <section className="cv-auto py-14 px-4 bg-gradient-to-b from-gray-950 to-black border-y border-gold-500/10" data-testid="home-featured-services">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
              UK-wide moving services from HomeGo Removals
            </h2>
            <p className="text-gray-400 mb-8 text-sm md:text-base max-w-3xl">
              Whether you&apos;re moving a 1-bed flat or a 25-desk office, our
              professional PAYE crew handles every stage. Tap any service below
              for full pricing, FAQs and instant booking — each service page
              has real photos from our moves, clear price tables and straight
              answers to the questions customers ask us most.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-featured-services-grid">
              {[
                {
                  slug: 'house-removals',
                  badge: '#1 most-booked',
                  h3: 'House Removals in the UK',
                  q: 'Need a house move done in a day?',
                  a: 'From 1-bed flats to 5+ bed detached homes — fully insured PAYE crew, fixed quote, free dismantle & reassemble, £50,000 cover.',
                  cta: 'View full House Removals UK guide →',
                  testid: 'home-feature-house-removals',
                },
                {
                  slug: 'office-relocation',
                  badge: 'Out-of-hours OK',
                  h3: 'Office Removals & Commercial Relocations UK',
                  q: 'Moving an office, warehouse or retail unit?',
                  a: 'Server-room safe, evenings & weekends to avoid downtime, IT-trolley equipped, full DSE risk assessments. From single desks to 100+ workstations.',
                  cta: 'View Office Removals UK page →',
                  testid: 'home-feature-office-relocation',
                },
                {
                  slug: 'packing-services',
                  badge: 'Materials included',
                  h3: 'Packing & Removals Services',
                  q: 'Want professional packing with no extra charge?',
                  a: 'Full or fragile-only — boxes, tape, bubble wrap, paper, china crates. Crystal, antiques, artwork and pianos all handled by trained packers in protective gloves.',
                  cta: 'View Packing & Removals Services →',
                  testid: 'home-feature-packing-services',
                },
                {
                  slug: 'house-removals#storage',
                  badge: 'Secure & insured',
                  h3: 'Storage Services',
                  q: 'Short-term or long-term storage between moves?',
                  a: `Secure climate-controlled units across our ${BRANCH_COUNT_WORD} branches. Bridge-the-gap storage for completion delays, summer student storage, and corporate document archive — all insured.`,
                  cta: 'View Storage Services →',
                  testid: 'home-feature-storage-services',
                },
              ].map((s) => (
                <Link
                  key={s.testid}
                  to={`/${s.slug}`}
                  className="group bg-black/60 border border-gold-500/25 hover:border-gold-500/60 rounded-xl p-5 transition-all hover:shadow-lg hover:shadow-gold-500/10"
                  data-testid={s.testid}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gold-400 bg-gold-400/10 border border-gold-400/30 rounded-full px-2 py-0.5">
                      {s.badge}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gold-400/60 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <h3 className="text-white font-bold text-lg leading-snug mb-2 group-hover:text-gold-400 transition-colors">
                    {s.h3}
                  </h3>
                  <p className="text-gray-300 text-sm font-semibold mb-1">{s.q}</p>
                  <p className="text-gray-400 text-xs leading-relaxed mb-3">{s.a}</p>
                  <span className="text-gold-400 text-xs font-semibold">{s.cta}</span>
                </Link>
              ))}
            </div>

            {/* JSON-LD — ItemList schema teaches Google these 4 hubs are
                the canonical service entrypoints on the homepage. Pairs
                with the visible H3 + Q + A text above so AI overviews
                (Bard / Gemini / SGE / Bing Chat) can quote the answers
                directly with attribution to your site. */}
            {/* Escape `<` in the serialized JSON so a stray "</script>" in
                any future data value can never break out of the <script>
                tag. React renders the child as a text node which is safe,
                but we keep dangerouslySetInnerHTML for parity with the
                other JSON-LD blocks and defense-in-depth. */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'ItemList',
                  name: 'HomeGo Removals — Featured UK Moving Services',
                  itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'House Removals UK', url: `${companyInfo.website}/house-removals` },
                    { '@type': 'ListItem', position: 2, name: 'Office Removals & Commercial Relocations UK', url: `${companyInfo.website}/office-relocation` },
                    { '@type': 'ListItem', position: 3, name: 'Packing & Removals Services', url: `${companyInfo.website}/packing-services` },
                    { '@type': 'ListItem', position: 4, name: 'Storage Services', url: `${companyInfo.website}/house-removals#storage` },
                  ],
                }).replace(/</g, '\\u003c'),
              }}
            />
          </div>
        </section>

        {/* SERVICES */}
        <section className="cv-auto py-14 px-4 bg-black">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Our Removal Services</h2>
            <p className="text-gray-400 mb-8 text-sm md:text-base">Click any service for detailed info, pricing guide and FAQs.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((s) => {
                const Icon = serviceIcons[s.icon] || Truck;
                return (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    className="group bg-gradient-to-b from-gray-900 to-black border border-gold-500/20 hover:border-gold-500/60 rounded-lg p-5 transition-all hover:shadow-lg hover:shadow-gold-500/10"
                    data-testid={`home-service-${s.slug}`}
                  >
                    <Icon className="w-7 h-7 text-gold-400 mb-3" />
                    <h3 className="text-white font-bold text-base mb-1.5 group-hover:text-gold-400 transition-colors">{s.name}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed mb-2">{s.short}</p>
                    <div className="text-gold-400 text-xs font-semibold inline-flex items-center gap-1">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* AREAS */}
        <section className="cv-auto py-14 px-4 bg-gradient-to-b from-black to-gray-950" data-testid="areas-section">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Areas We Cover</h2>
            <p className="text-gray-400 mb-8 text-sm md:text-base">
              {locations.length} dedicated location pages covering counties, villages and surrounding areas across our service region.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {locations.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/removals-${loc.slug}`}
                  className="bg-gradient-to-br from-gray-900 to-black border border-gold-500/20 hover:border-gold-500/50 rounded-lg p-4 transition-colors group"
                  data-testid={`home-area-${loc.slug}`}
                >
                  <div className="text-gold-400 text-xs font-semibold mb-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {loc.county}
                  </div>
                  <div className="text-white font-bold group-hover:text-gold-400 transition-colors">{loc.name}</div>
                  <div className="text-gray-400 text-xs mt-1">{loc.postcodes}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>


        {/* TIKTOK FEED — latest real-move videos + follow CTA */}
        <Suspense fallback={<div style={{ minHeight: 300 }} />}>
          <TikTokFeed />
        </Suspense>

        {/* REGIONAL DEPOT SHOWCASE — placed directly after the AI / Voice-search
            block so both Google's SGE crawler and human visitors see the
            single strongest trust signal on the site: our real premises with
            the full HomeGo fleet parked out front. Wide 16:9 crop for maximum
            visual impact on desktop, still scales down cleanly on mobile.
            Compressed via wsrv.nl WebP (322 KB → ~78 KB @ 1200w).
            Alt + figcaption double up as answer copy for AI Overviews when
            they scrape "what does HomeGo Removals fleet look like" queries. */}
        <section className="cv-auto py-14 px-4 bg-black" data-testid="home-depot-showcase">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-gold-400" />
              <span className="text-[11px] text-gold-400 font-bold uppercase tracking-[0.18em]">Our Regional Depot & Fleet</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Real premises. Real HomeGo-branded vehicles. Fully insured.
            </h2>
            <p className="text-sm md:text-base text-gray-400 mb-6 max-w-3xl">
              Every job leaves our HomeGo Removals & Packing Regional Depot in a branded, fully-equipped truck or van — not an unmarked hire vehicle. Your belongings can be stored overnight in our secure depot on multi-day and long-distance moves.
            </p>
            <figure className="rounded-xl overflow-hidden border-2 border-gold-500/20 shadow-2xl">
              <img
                src={compressImg(BRAND_PHOTOS.regionalDepot.raw, 1600, 74)}
                srcSet={responsiveSrcSet(BRAND_PHOTOS.regionalDepot.raw, 74)}
                sizes="(max-width: 640px) 100vw, (max-width: 1280px) 100vw, 1200px"
                alt={BRAND_PHOTOS.regionalDepot.alt}
                width={1600}
                height={960}
                loading="lazy"
                decoding="async"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = BRAND_PHOTOS.regionalDepot.raw; }}
                className="w-full h-auto aspect-[16/9] object-cover"
                data-testid="home-depot-showcase-img"
              />
              <figcaption className="bg-gradient-to-b from-gray-950 to-black border-t border-gold-500/20 px-5 py-4 text-sm text-gray-300 leading-relaxed">
                <strong className="text-white">Pictured:</strong> the HomeGo Regional Depot with our full working fleet — a HomeGo-branded <strong className="text-white">DAF 7-tonne curtain-side lorry</strong> (ideal for 4+ bedroom houses and long-distance UK moves) alongside two <strong className="text-white">long-wheelbase Mercedes Maxi-mover Luton vans</strong> (perfect for 1-4 bedroom homes, small offices and both short and long-distance jobs). Behind the vehicles is our secure overnight-storage depot. Every truck is fully insured, GPS-tracked and kitted with blankets, straps, dollies, wardrobe cartons and packing kit — nothing extra to hire, nothing left to chance.
              </figcaption>
            </figure>
          </div>
        </section>

        {/* FAQ */}
        <section className="cv-auto py-14 px-4 bg-gradient-to-b from-black to-gray-950">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {homepageFAQs.map((f, i) => (
                <div key={f.q} className="bg-black/40 border border-gold-500/20 rounded-lg p-5" data-testid={`home-faq-${i}`}>
                  <h3 className="text-white font-bold mb-2">{f.q}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="cv-auto py-14 px-4 bg-gradient-to-r from-gold-500/10 via-gold-400/5 to-gold-500/10 border-y border-gold-500/20">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">Ready to move?</h2>
            <p className="text-gray-300 mb-6">Free 2-minute quote. We call you back within 2 minutes, 24/7.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="#quote" className="bg-gradient-to-r from-gold-500 to-gold-400 text-black font-bold px-7 py-3.5 rounded-md shadow-lg shadow-gold-500/20">Get Free Quote</a>
              <a href={companyInfo.mobileHref} className="border-2 border-gold-500 text-white hover:bg-gold-500/10 font-bold px-7 py-3.5 rounded-md">
                <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call {companyInfo.mobile}</span>
              </a>
            </div>
            <div className="mt-6 flex items-center justify-center gap-1 text-gold-400">
              <Suspense fallback={null}><GoogleReviews variant="summary" /></Suspense>
            </div>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <MobileStickyCTA />
    </>
  );
}
